import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity,
  Award,
  Banknote,
  CalendarDays,
  CheckSquare,
  FileText,
  HandCoins,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  ShieldCheck,
  StickyNote,
} from "lucide-react";
import { Card } from "@/design-system/components/ui";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { CertificationsTab } from "@/components/certifications/CertificationsTab";
import { useInvestor, useInvestorCommitments } from "@/hooks/capitalMarkets/useInvestors";
import { useInvestorName } from "./useInvestorName";
import { InvestorStatusBadge, KpiCard } from "../shared/components";
import { formatCurrency, humanizeStatus } from "../shared/format";
import { InvestorOverviewTab } from "./tabs/OverviewTab";
import { CommitmentsTab } from "./tabs/CommitmentsTab";
import { DistributionsTab } from "./tabs/DistributionsTab";
import { AccreditationKycTab } from "./tabs/AccreditationKycTab";

type TabId =
  | "overview"
  | "commitments"
  | "distributions"
  | "accreditation"
  | "certifications"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "commitments",
  "distributions",
  "accreditation",
  "certifications",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
];

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "commitments", label: "Commitments", icon: HandCoins },
  { key: "distributions", label: "Distributions", icon: Banknote },
  { key: "accreditation", label: "Accreditation & KYC", icon: ShieldCheck },
  { key: "certifications", label: "Certifications", icon: Award },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "tasks", label: "Tasks", icon: CheckSquare },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "communications", label: "Communications", icon: MessageCircle },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "activity", label: "Activity", icon: Activity },
];

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

const ACTIVE_STATUSES = new Set(["Indication", "Committed", "Funded"]);

export function InvestorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const investorQuery = useInvestor(id);
  const investor = investorQuery.data;
  const { name, counterpartyHref, counterpartyKind } = useInvestorName(investor);
  const commitmentsQuery = useInvestorCommitments(
    { investorId: id, pageSize: 200 },
    !!id,
  );

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  const kpis = useMemo(() => {
    const commitments = commitmentsQuery.data?.data ?? [];
    let committed = 0;
    let funded = 0;
    let active = 0;
    for (const c of commitments) {
      committed += c.committedAmount;
      funded += c.fundedAmount;
      if (ACTIVE_STATUSES.has(c.status)) active += 1;
    }
    return { committed, funded, active };
  }, [commitmentsQuery.data]);

  if (investorQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading investor…</p>
      </Card>
    );
  }

  if (!investor) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Investor not found</h2>
        <p className="text-small text-gray-mid">
          The investor you requested was not found or you don&apos;t have
          permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/capital-markets/investors")}
        >
          Back to investors
        </button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex flex-wrap items-center gap-space-3">
        <h1 className="font-display font-bold text-h2 text-onyx m-0">{name}</h1>
        <span className="text-caption font-display uppercase tracking-label text-gray-mid">
          {humanizeStatus(investor.investorType)}
        </span>
        <InvestorStatusBadge status={investor.status} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3">
        <KpiCard
          label="Total Committed"
          value={formatCurrency(kpis.committed)}
          hint="Across all pools"
        />
        <KpiCard
          label="Total Funded"
          value={formatCurrency(kpis.funded)}
          hint="Across all pools"
        />
        <KpiCard label="Active Commitments" value={kpis.active} />
      </div>

      <EntityTabBar
        ariaLabel="Investor sections"
        testIdPrefix="investor"
        items={TAB_ITEMS}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          <InvestorOverviewTab
            investor={investor}
            displayName={name}
            counterpartyHref={counterpartyHref}
            counterpartyKind={counterpartyKind}
            onOpenCertifications={() => setTab("accreditation")}
          />
        )}
        {tab === "commitments" && <CommitmentsTab investorId={investor.id} />}
        {tab === "distributions" && (
          <DistributionsTab investorId={investor.id} />
        )}
        {tab === "accreditation" && (
          <AccreditationKycTab
            investor={investor}
            onOpenCertifications={() => setTab("certifications")}
          />
        )}
        {tab === "certifications" && (
          <CertificationsTab
            entityType="Investor"
            entityId={investor.id}
            currentStage={investor.status}
          />
        )}
        {tab === "documents" && (
          <DocumentPanel
            ownerType="investor"
            ownerId={investor.id}
            rootName={name}
          />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Investor"
            relatedEntityId={investor.id}
            addButtonLabel="+ Add task"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Investor"
            relatedEntityId={investor.id}
            entityLabel={name}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="investor"
            resourceId={investor.id}
            entityLabel={name}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="investor"
            ownerId={investor.id}
            ownerLabel={name}
          />
        )}
        {tab === "notes" && (
          <NotesTabPanel ownerType="investor" ownerId={investor.id} />
        )}
        {tab === "activity" && (
          <ActivityTabPanel ownerType="investor" ownerId={investor.id} />
        )}
      </div>
    </div>
  );
}
